// EditTalent
import { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { getTalentById, updateTalent, UserContext, useFreshManagers, updateUser, createTalentUpdate, createUserUpdate, convertNestedEmptyToNull } from '../services/firebase';
import { Currency, PlatformData, Prices, Talent } from '../shared/types';
import { Controller, UseFormRegister, useForm } from 'react-hook-form';
import { timestampOrDateToDate } from '../utils/dates';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/react';

// Components
import Accordion, { AccordionGroup } from '../components/Accordion';
import LayoutOnboarding from '../components/LayoutOnboarding';
import CountrySelect from '../components/CountrySelect';
import ManagerSelect from '../components/ManagerSelect';
import AgeBracketSelect from '../components/AgeBracketSelect';
import GenderSelect from '../components/GenderSelect';
import LanguageSelect from '../components/LanguageSelect';
import GenreSelect from '../components/GenreSelect';
import ContentTagSelect from '../components/ContentTagSelect';
import { Alert, AlertAction, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { FormCheckbox } from '@/components/forms/FormCheckbox';
import { FormInput } from '@/components/forms/FormInput';
import { FormSelect } from '@/components/forms/FormSelect';
import { Field, FieldGroup, FieldLabel, FieldSeparator } from '@/components/ui/field';
import { SelectItem } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useSkeletonPhases } from '@/hooks/useSkeletonPhases';
import { cn } from "cn";
import { XIcon } from 'lucide-react';

type FormAlert = {
    message: string;
    detail?: string;
} | null;

type CreatorTalentForm = {
    gender?: string;
    contentLanguage?: string;
    favoriteGenre?: string;
    contentTag0?: string;
    contentTag1?: string;
    contentTag2?: string;
    contentTag3?: string;
    contentTag4?: string;
    platformData?: PlatformData;
};

type GrailTeamTalentForm = CreatorTalentForm & {
    name?: string;
    exclusiveCreator?: Talent["exclusiveCreator"] | "";
    country?: string | { code?: string; label?: string };
    birthday?: string | Date | null;
    currency?: Currency | "";
    managerIds?: string[];
    airtableManagerIds?: Array<string | undefined>;
    prices?: Partial<Record<keyof Prices, string | number | null>>;
    hideUser?: boolean;
    deleted?: boolean;
};

export default function EditTalent() {
    const user = useContext(UserContext);
    const isGrailTeam = Boolean(user?.roles?.manager || user?.roles?.admin || user?.roles?.externalManager || user?.roles?.superAdmin);
    const [talent, setTalent] = useState<Talent>();
    const { talentId } = useParams<{ talentId: string }>();
    const [formAlert, setFormAlert] = useState<FormAlert>(null);
    const talentUsername = talent?.platformData?.TikTok?.username || talent?.platformData?.Instagram?.username || talent?.platformData?.YouTube?.username;
    const [basicsAccordionOpen, setBasicsAccordionOpen] = useState(false);
    const [tiktokAccordionOpen, setTiktokAccordionOpen] = useState(false);
    const [instagramAccordionOpen, setInstagramAccordionOpen] = useState(false);
    const [youtubeAccordionOpen, setYoutubeAccordionOpen] = useState(false);

    const managersQuery = useFreshManagers({ enabled: isGrailTeam });
    const managers = managersQuery.flatData;
    const phase = useSkeletonPhases(!talent);

    const {
        register,
        control,
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm<GrailTeamTalentForm>({ mode: 'all' });

    async function submitForm(formData: GrailTeamTalentForm) {
        // console.log(`formData`, formData);
        if (!talentId) {
            return;
        }

        const {
            country: countryField,
            prices: rawPrices,
            managerIds,
            birthday,
            contentTag0,
            contentTag1,
            contentTag2,
            contentTag3,
            contentTag4,
            ...owned
        } = formData;
        const contentTags = [contentTag0, contentTag1, contentTag2, contentTag3, contentTag4].filter((tag): tag is string => Boolean(tag));

        let country = null;
        if (countryField) {
            if (typeof countryField === 'string') {
                country = countryField;
            } else if (countryField.label) {
                country = countryField.label;
            } else if (countryField.code) {
                country = countryField.code;
            }
        }

        const prices: Prices = {};
        Object.keys(rawPrices || {}).forEach(function (key) {
            prices[key as keyof Prices] = parseInt(String(rawPrices?.[key as keyof Prices])) || null;
        });

        const res = await updateTalent(createTalentUpdate({
            id: talentId,
            ...convertNestedEmptyToNull({
                ...owned,
                country,
                contentTags,
                lastUpdate: 'EDIT_TALENT_FORM',
                managerIds: managerIds || [],
                managers: (managerIds || []).map((managerId) => {
                    const managerDoc = managers.find((manager) => manager.id === managerId);
                    return {
                        id: managerId,
                        airtableId: managerDoc?.managerAirtableId || '',
                        firstName: managerDoc?.firstName || '',
                        lastName: managerDoc?.lastName || '',
                        email: managerDoc?.email || '',
                        phone: managerDoc?.phone || '',
                    };
                }),
                prices,
                ...(birthday ? { birthday: dayjs(birthday).toDate() } : {}),
                platformData: {
                    ...(owned.platformData || {}),
                    TikTok: {
                        ...(owned.platformData?.TikTok || {}),
                        failedCrawls: 0,
                    },
                },
            }),
        }));

        if (res.success) {
            setFormAlert(null);
            toast.add({
                type: 'success',
                title: 'Changes saved',
                description: 'Your changes have been saved!',
            });
        } else {
            // console.log(res.error);
            // console.log(`Failed Saving:`, JSON.stringify(talentData));

            setFormAlert({
                message: `There was an issue saving your changes... ${JSON.stringify(res.error)}`,
                detail: res.error.toString(),
            });
            Sentry.captureException(res.error);
            document?.getElementById('EditTalentHeader')?.scrollIntoView();
        }
    }

    async function submitFormCreator(formData: CreatorTalentForm) {
        // console.log(`formData!`, formData);
        if (!talentId) {
            return;
        }

        const {
            contentTag0,
            contentTag1,
            contentTag2,
            contentTag3,
            contentTag4,
            ...owned
        } = formData;
        const contentTags = [contentTag0, contentTag1, contentTag2, contentTag3, contentTag4].filter((tag): tag is string => Boolean(tag));

        const res = await updateTalent(createTalentUpdate({
            id: talentId,
            ...convertNestedEmptyToNull({
                ...owned,
                contentTags,
                lastUpdate: 'EDIT_TALENT_FORM',
                platformData: {
                    ...(owned.platformData || {}),
                    TikTok: {
                        ...(owned.platformData?.TikTok || {}),
                        failedCrawls: 0,
                    },
                },
            }),
        }));

        const isFirstTimeEditing = !user?.talentFieldsEdited;
        let userSuccess = false;
        if (isFirstTimeEditing && user?.id) {
            const userRes = await updateUser(createUserUpdate({
                id: user.id,
                talentFieldsEdited: true,
            }));
            if (userRes.success) {
                userSuccess = true;
            }
        }

        if (isFirstTimeEditing && res.success && userSuccess) {
            window.location.href = '/';
        } else if (isFirstTimeEditing && res.success && !userSuccess) {
            setFormAlert({
                message: 'An error occurred updating your account status. Please try again.',
            });
            document?.getElementById('EditTalentHeader')?.scrollIntoView();
        } else if (res.success) {
            setFormAlert(null);
            toast.add({
                type: 'success',
                title: 'Changes saved',
                description: 'Your changes have been saved!',
            });
        } else {
            console.log(res.error);

            setFormAlert({
                message: `There was an issue saving your changes... ${JSON.stringify(res.error)}`,
                detail: res.error.toString(),
            });
            Sentry.captureException(res.error);
            document?.getElementById('EditTalentHeader')?.scrollIntoView();
        }
    }

    // A little hacky, but it works.
    function checkFormValidity(e: React.MouseEvent<HTMLButtonElement>) {
        const form = (e.target as HTMLButtonElement).closest('form');
        const isValid = form?.checkValidity();

        if (form && (!isValid || Object.keys(errors).length)) {
            setBasicsAccordionOpen(true);
            setTiktokAccordionOpen(true);
            setInstagramAccordionOpen(true);
            setYoutubeAccordionOpen(true);

            setTimeout(() => {
                for (let i = 0; i < form.elements.length; i++) {
                    const element = form.elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
                    if ((element.tagName === 'INPUT' || element.tagName === 'SELECT' || element.tagName === 'TEXTAREA') && !element.disabled) {
                        const elValid = element.reportValidity() && !Object.keys(errors).includes(element.name);

                        if (!elValid) {
                            console.log(`elValid`, element.name, elValid, element.getBoundingClientRect().top);
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            break;
                        }
                    }
                }
            }, 500);
        }

        return false;
    }

    useEffect(() => {
        if (!talent) {
            getInitialProps();
        }

        async function getInitialProps() {
            if (talentId) {
                const newTalent = await getTalentById(talentId);
                setTalent(newTalent);
            }
        }
    }, [talentId, talent]);

    return (
        <LayoutOnboarding className="bg-white">
            {phase === "showSkeleton" ? (
                <div id="EditTalentHeader" className="mb-6 flex items-center justify-center">
                    <Skeleton className="mr-4 h-20 w-20" />
                    <Skeleton className="h-7 w-40" />
                </div>
            ) : talentId && talent ? (
                <div id="EditTalentHeader" className="mb-6 flex items-center justify-center">
                    {talent?.imgUrl ? <img src={talent.imgUrl} alt="Creator" className="mr-4" width="80" height="80" /> : null}
                    <h2 className="font-semibold">{talentUsername ? <>@{talentUsername}</> : 'Creator Without Username...'}</h2>
                </div>
            ) : null}

            <div className="mx-auto max-w-[800px]">
                {formAlert ? (
                    <Alert
                        variant="destructive"
                        className="mb-4 text-left"
                    >
                        <AlertDescription>
                            {formAlert.message}
                            {formAlert.detail ? <div className="mt-4 text-tiny/4">{formAlert.detail}</div> : null}
                        </AlertDescription>
                        <AlertAction>
                            <Button size="icon-xs" variant="ghost" onClick={() => setFormAlert(null)}>
                                <XIcon />
                                <span className="sr-only">Close</span>
                            </Button>
                        </AlertAction>
                    </Alert>
                ) : null}

                {phase === "showSkeleton" ? (
                    <AccordionGroup>
                        <Accordion title="Basic Settings" expanded={false}>{null}</Accordion>
                        <Accordion
                            title={
                                <div className="flex items-center">
                                    <img className="mr-2 block size-8 rounded" src="/icon-tiktok.svg" alt="TikTok" />
                                    TikTok
                                </div>
                            }
                            expanded={false}
                        >
                            {null}
                        </Accordion>
                        <Accordion
                            title={
                                <div className="flex items-center">
                                    <img className="mr-2 block size-8 rounded" src="/icon-instagram.svg" alt="Instagram" />
                                    Instagram
                                </div>
                            }
                            expanded={false}
                        >
                            {null}
                        </Accordion>
                        {isGrailTeam ? (
                            <Accordion
                                title={
                                    <div className="flex items-center">
                                        <img className="mr-2 block size-8 rounded" src="/icon-youtube.svg" alt="YouTube" />
                                        YouTube
                                    </div>
                                }
                                expanded={false}
                            >
                                {null}
                            </Accordion>
                        ) : null}
                        {isGrailTeam ? (
                            <Accordion title="Hide/Delete" expanded={false}>{null}</Accordion>
                        ) : null}
                    </AccordionGroup>
                ) : talentId && talent ? (
                    <>
                        <form className="text-left" onSubmit={handleSubmit(isGrailTeam ? submitForm : submitFormCreator)}>
                            <AccordionGroup>
                                <Accordion title="Basic Settings" expanded={basicsAccordionOpen} onChange={() => setBasicsAccordionOpen(!basicsAccordionOpen)}>
                                    <FieldGroup>
                                        {isGrailTeam && (
                                            <>
                                                <div className="mb-4">
                                                    <strong>Airtable ID:</strong> {talent.id}
                                                </div>

                                                <Field className={cn(errors.managerIds && 'text-destructive')} data-invalid={Boolean(errors.managerIds) || undefined}>
                                                    <FieldLabel>Manager(s)</FieldLabel>
                                                    <Controller
                                                        control={control}
                                                        name="managerIds"
                                                        defaultValue={talent?.managerIds || []}
                                                        render={({ field: { onChange, value } }) => (
                                                            <ManagerSelect
                                                                managers={managers}
                                                                value={value ?? []}
                                                                disabled={!user?.roles?.admin}
                                                                onChange={(e: any, managerIds: string[]) => {
                                                                    onChange(managerIds);
                                                                    // set the hidden field for the airtableManagerIds
                                                                    setValue(
                                                                        'airtableManagerIds',
                                                                        managerIds.map((id) => managers.find((manager) => manager.id === id)?.managerAirtableId)
                                                                    );
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Field>

                                                <Controller
                                                    control={control}
                                                    name="exclusiveCreator"
                                                    defaultValue={talent.exclusiveCreator || ''}
                                                    render={({ field }) => (
                                                        <FormSelect
                                                            label="Exclusivity"
                                                            value={field.value || null}
                                                            onChange={(value) => field.onChange(value ?? '')}
                                                            placeholder="Select Exclusivity Status"
                                                        >
                                                            <SelectItem value="Exclusive">Exclusive</SelectItem>
                                                            <SelectItem value="Non-Exclusive">Non-Exclusive</SelectItem>
                                                        </FormSelect>
                                                    )}
                                                />

                                                <input
                                                    type="hidden"
                                                    {...register('airtableManagerIds', {
                                                        value: talent?.airtableManagerIds,
                                                    })}
                                                />

                                                <FormInput label="Name" type="text" {...register('name', { value: talent.name })} />

                                                <Field>
                                                    <FieldLabel>Country</FieldLabel>
                                                    <Controller
                                                        control={control}
                                                        name="country"
                                                        defaultValue={talent?.country || ''}
                                                        render={({ field: { onChange, value } }) => <CountrySelect onChange={onChange} value={value} required={true} />}
                                                    />
                                                </Field>

                                                <FormInput
                                                    label="Birthday"
                                                    type="date"
                                                    {...register('birthday', {
                                                        value: talent.birthday ? timestampOrDateToDate(talent.birthday) : null,
                                                    })}
                                                />

                                                <Controller
                                                    control={control}
                                                    name="currency"
                                                    defaultValue={talent.currency || ''}
                                                    render={({ field }) => (
                                                        <FormSelect
                                                            label="Creator's Currency"
                                                            value={field.value || null}
                                                            onChange={(value) => field.onChange(value ?? '')}
                                                            placeholder="Select a Currency"
                                                        >
                                                            <SelectItem value="USD">USD</SelectItem>
                                                            <SelectItem value="GBP">GBP</SelectItem>
                                                        </FormSelect>
                                                    )}
                                                />
                                            </>
                                        )}

                                        <Field>
                                            <FieldLabel>
                                                Gender
                                                {!isGrailTeam ? <span className="text-destructive"> *</span> : null}
                                            </FieldLabel>
                                            <Controller
                                                control={control}
                                                name="gender"
                                                defaultValue={talent?.gender || ''}
                                                render={({ field: { onChange, value } }) => <GenderSelect onChange={onChange} value={value} required={!isGrailTeam} />}
                                            />
                                        </Field>

                                        <Field>
                                            <FieldLabel>
                                                Content Language
                                                {!isGrailTeam ? <span className="text-destructive"> *</span> : null}
                                            </FieldLabel>
                                            <Controller
                                                control={control}
                                                name="contentLanguage"
                                                defaultValue={talent?.contentLanguage || ''}
                                                render={({ field: { onChange, value } }) => <LanguageSelect onChange={onChange} value={value} required={!isGrailTeam} />}
                                            />
                                        </Field>

                                        <Field>
                                            <FieldLabel>
                                                Favorite Genre
                                                {Boolean(!isGrailTeam && talent?.platformData?.TikTok?.username) ? <span className="text-destructive"> *</span> : null}
                                            </FieldLabel>
                                            <Controller
                                                control={control}
                                                name="favoriteGenre"
                                                defaultValue={talent?.favoriteGenre || ''}
                                                render={({ field: { onChange, value } }) => <GenreSelect onChange={onChange} value={value} required={!isGrailTeam} />}
                                            />
                                        </Field>

                                        {(["contentTag0", "contentTag1", "contentTag2", "contentTag3", "contentTag4"] as const).map((name, i) => (
                                            <Field key={name}>
                                                <FieldLabel>
                                                    Content Tag {i + 1}
                                                    {Boolean(!isGrailTeam && i < 2) ? <span className="text-destructive"> *</span> : null}
                                                </FieldLabel>
                                                <Controller
                                                    control={control}
                                                    name={name}
                                                    defaultValue={talent?.contentTags?.[i] || ''}
                                                    render={({ field: { onChange, value } }) => <ContentTagSelect onChange={onChange} value={value} required={!isGrailTeam && i < 2} />}
                                                />
                                            </Field>
                                        ))}
                                    </FieldGroup>
                                </Accordion>

                                <Accordion
                                    title={
                                        <div className="flex items-center">
                                            <img className="mr-2 block size-8 rounded" src="/icon-tiktok.svg" alt="TikTok" />
                                            TikTok
                                        </div>
                                    }
                                    expanded={tiktokAccordionOpen}
                                    onChange={() => setTiktokAccordionOpen(!tiktokAccordionOpen)}
                                >
                                    <FieldGroup>
                                        {isGrailTeam && (
                                            <>
                                                <h3 className="my-3 font-semibold">Account Information</h3>
                                                <FormInput
                                                    label="TikTok User"
                                                    type="text"
                                                    error={
                                                        (
                                                            errors as {
                                                                platformData?: {
                                                                    TikTok?: { username?: { message?: string } };
                                                                };
                                                            }
                                                        ).platformData?.TikTok?.username?.message
                                                    }
                                                    {...register('platformData.TikTok.username', {
                                                        value: talent?.platformData?.TikTok?.username,
                                                        pattern: {
                                                            value: /^[a-zA-Z0-9-_.]*$/,
                                                            message: 'Do not include @ or special characters',
                                                        },
                                                    })}
                                                />

                                                <FieldSeparator />

                                                <h3 className="my-3 font-semibold">Prices</h3>

                                                <FormInput
                                                    label="Brand Price"
                                                    type="number"
                                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                                    {...register('prices.brandPriceTikTok', {
                                                        value: talent?.prices?.brandPriceTikTok,
                                                    })}
                                                />

                                                <FormInput
                                                    label="TikTok Song Price"
                                                    type="number"
                                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                                    {...register('prices.songPriceTikTok', {
                                                        value: talent?.prices?.songPriceTikTok,
                                                    })}
                                                />

                                                <FieldSeparator />
                                            </>
                                        )}

                                        <h3 className="my-3 font-semibold">Audience Stats</h3>

                                        <FormInput
                                            label="Female Follower Percentage"
                                            required={Boolean(!isGrailTeam && talent?.platformData?.TikTok?.username)}
                                            {...percentageInputProps({
                                                field: 'platformData.TikTok.femaleFollowerPct',
                                                value: talent?.platformData?.TikTok?.femaleFollowerPct,
                                                register,
                                            })}
                                        />

                                        <FormInput
                                            label="Male Follower Percentage"
                                            required={Boolean(!isGrailTeam && talent?.platformData?.TikTok?.username)}
                                            {...percentageInputProps({
                                                field: 'platformData.TikTok.maleFollowerPct',
                                                value: talent?.platformData?.TikTok?.maleFollowerPct,
                                                register,
                                            })}
                                        />

                                        <Field>
                                            <FieldLabel>
                                                Top Follower Location
                                                {Boolean(!isGrailTeam && talent?.platformData?.TikTok?.username) ? <span className="text-destructive"> *</span> : null}
                                            </FieldLabel>
                                            <Controller
                                                control={control}
                                                name="platformData.TikTok.topFollowerLocation"
                                                defaultValue={talent?.platformData?.TikTok?.topFollowerLocation || ''}
                                                render={({ field: { onChange, value } }) => (
                                                    <CountrySelect onChange={onChange} value={value} required={Boolean(!isGrailTeam && talent?.platformData?.TikTok?.username)} />
                                                )}
                                            />
                                        </Field>

                                        <FormInput
                                            label="Top Follower Location Percentage"
                                            required={Boolean(!isGrailTeam && talent?.platformData?.TikTok?.username)}
                                            {...percentageInputProps({
                                                field: 'platformData.TikTok.topFollowerLocationPct',
                                                value: talent?.platformData?.TikTok?.topFollowerLocationPct,
                                                register,
                                            })}
                                        />

                                        <Field>
                                            <FieldLabel>
                                                Top Follower Age Bracket
                                                {Boolean(!isGrailTeam && talent?.platformData?.TikTok?.username) ? <span className="text-destructive"> *</span> : null}
                                            </FieldLabel>
                                            <Controller
                                                control={control}
                                                name="platformData.TikTok.topFollowerAgeBracket"
                                                defaultValue={talent?.platformData?.TikTok?.topFollowerAgeBracket || ''}
                                                render={({ field: { onChange, value } }) => (
                                                    <AgeBracketSelect onChange={onChange} value={value} required={Boolean(!isGrailTeam && talent?.platformData?.TikTok?.username)} />
                                                )}
                                            />
                                        </Field>

                                        <FormInput
                                            label="Top Follower Age Bracket Percentage"
                                            required={Boolean(!isGrailTeam && talent?.platformData?.TikTok?.username)}
                                            {...percentageInputProps({
                                                field: 'platformData.TikTok.topFollowerAgeBracketPct',
                                                value: talent?.platformData?.TikTok?.topFollowerAgeBracketPct,
                                                register,
                                            })}
                                        />
                                    </FieldGroup>
                                </Accordion>

                                <Accordion
                                    title={
                                        <div className="flex items-center">
                                            <img className="mr-2 block size-8 rounded" src="/icon-instagram.svg" alt="Instagram" />
                                            Instagram
                                        </div>
                                    }
                                    expanded={instagramAccordionOpen}
                                    onChange={() => setInstagramAccordionOpen(!instagramAccordionOpen)}
                                >
                                    <FieldGroup>
                                        {isGrailTeam && (
                                            <>
                                                <h3 className="my-3 font-semibold">Account Information</h3>
                                                <FormInput
                                                    label="Instagram User"
                                                    type="text"
                                                    error={
                                                        (
                                                            errors as {
                                                                platformData?: {
                                                                    Instagram?: { username?: { message?: string } };
                                                                };
                                                            }
                                                        ).platformData?.Instagram?.username?.message
                                                    }
                                                    {...register('platformData.Instagram.username', {
                                                        value: talent?.platformData?.Instagram?.username,
                                                        pattern: {
                                                            value: /^[a-zA-Z0-9-_.]*$/,
                                                            message: 'Do not include @ or special characters',
                                                        },
                                                    })}
                                                />

                                                <FormInput
                                                    label="Instagram Followers"
                                                    type="number"
                                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                                    error={
                                                        (
                                                            errors as {
                                                                platformData?: {
                                                                    Instagram?: { followers?: { message?: string } };
                                                                };
                                                            }
                                                        ).platformData?.Instagram?.followers?.message
                                                    }
                                                    {...register('platformData.Instagram.followers', {
                                                        value: talent?.platformData?.Instagram?.followers,
                                                        pattern: {
                                                            value: /^[0-9]*$/,
                                                            message: 'Enter a valid integer without special characters',
                                                        },
                                                    })}
                                                />

                                                <FieldSeparator />

                                                {/* <h3 className="font-semibold">Instagram Prices</h3> */}
                                                <h3 className="my-3 font-semibold">Brand Prices</h3>

                                                <FormInput
                                                    label="Reel"
                                                    description="Default Brand Price"
                                                    type="number"
                                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                                    {...register('prices.brandPriceInstagramReel', {
                                                        value: talent?.prices?.brandPriceInstagramReel,
                                                    })}
                                                />

                                                <FormInput
                                                    label="Grid Post"
                                                    type="number"
                                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                                    {...register('prices.brandPriceInstagramPost', {
                                                        value: talent?.prices?.brandPriceInstagramPost,
                                                    })}
                                                />

                                                <FormInput
                                                    label="3-Frame Story"
                                                    type="number"
                                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                                    {...register('prices.brandPriceInstagramStory', {
                                                        value: talent?.prices?.brandPriceInstagramStory,
                                                    })}
                                                />

                                                <h3 className="mt-6 mb-3 font-semibold">Song Price</h3>

                                                <FormInput
                                                    label="IG Song Price"
                                                    type="number"
                                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                                    {...register('prices.songPriceInstagram', {
                                                        value: talent?.prices?.songPriceInstagram,
                                                    })}
                                                />

                                                <FieldSeparator />
                                            </>
                                        )}

                                        <h3 className="my-3 font-semibold">Audience Stats</h3>

                                        <FormInput
                                            label="Female Follower Percentage"
                                            required={Boolean(!isGrailTeam && talent?.platformData?.Instagram?.username)}
                                            {...percentageInputProps({
                                                field: 'platformData.Instagram.femaleFollowerPct',
                                                value: talent?.platformData?.Instagram?.femaleFollowerPct,
                                                register,
                                            })}
                                        />

                                        <FormInput
                                            label="Male Follower Percentage"
                                            required={Boolean(!isGrailTeam && talent?.platformData?.Instagram?.username)}
                                            {...percentageInputProps({
                                                field: 'platformData.Instagram.maleFollowerPct',
                                                value: talent?.platformData?.Instagram?.maleFollowerPct,
                                                register,
                                            })}
                                        />

                                        <Field>
                                            <FieldLabel>
                                                Top Follower Location
                                                {Boolean(!isGrailTeam && talent?.platformData?.Instagram?.username) ? <span className="text-destructive"> *</span> : null}
                                            </FieldLabel>
                                            <Controller
                                                control={control}
                                                name="platformData.Instagram.topFollowerLocation"
                                                defaultValue={talent?.platformData?.Instagram?.topFollowerLocation || ''}
                                                render={({ field: { onChange, value } }) => (
                                                    <CountrySelect onChange={onChange} value={value} required={Boolean(!isGrailTeam && talent?.platformData?.Instagram?.username)} />
                                                )}
                                            />
                                        </Field>

                                        <FormInput
                                            label="Top Follower Location Percentage"
                                            required={Boolean(!isGrailTeam && talent?.platformData?.Instagram?.username)}
                                            {...percentageInputProps({
                                                field: 'platformData.Instagram.topFollowerLocationPct',
                                                value: talent?.platformData?.Instagram?.topFollowerLocationPct,
                                                register,
                                            })}
                                        />

                                        <Field>
                                            <FieldLabel>
                                                Top Follower Age Bracket
                                                {Boolean(!isGrailTeam && talent?.platformData?.Instagram?.username) ? <span className="text-destructive"> *</span> : null}
                                            </FieldLabel>
                                            <Controller
                                                control={control}
                                                name="platformData.Instagram.topFollowerAgeBracket"
                                                defaultValue={talent?.platformData?.Instagram?.topFollowerAgeBracket || ''}
                                                render={({ field: { onChange, value } }) => (
                                                    <AgeBracketSelect onChange={onChange} value={value} required={Boolean(!isGrailTeam && talent?.platformData?.Instagram?.username)} />
                                                )}
                                            />
                                        </Field>

                                        <FormInput
                                            label="Top Follower Age Bracket Percentage"
                                            required={Boolean(!isGrailTeam && talent?.platformData?.Instagram?.username)}
                                            {...percentageInputProps({
                                                field: 'platformData.Instagram.topFollowerAgeBracketPct',
                                                value: talent?.platformData?.Instagram?.topFollowerAgeBracketPct,
                                                register,
                                            })}
                                        />
                                    </FieldGroup>
                                </Accordion>

                                {isGrailTeam && (
                                    <Accordion
                                        title={
                                            <div className="flex items-center">
                                                <img className="mr-2 block size-8 rounded" src="/icon-youtube.svg" alt="YouTube" />
                                                YouTube
                                            </div>
                                        }
                                        expanded={youtubeAccordionOpen}
                                        onChange={() => setYoutubeAccordionOpen(!youtubeAccordionOpen)}
                                    >
                                        <FieldGroup>
                                            <>
                                                <h3 className="my-3 font-semibold">Account Information</h3>

                                                <FormInput
                                                    label="YouTube Channel Handle"
                                                    type="text"
                                                    error={
                                                        (
                                                            errors as {
                                                                platformData?: {
                                                                    YouTube?: { username?: { message?: string } };
                                                                };
                                                            }
                                                        ).platformData?.YouTube?.username?.message
                                                    }
                                                    {...register('platformData.YouTube.username', {
                                                        value: talent?.platformData?.YouTube?.username,
                                                        pattern: {
                                                            value: /^[a-zA-Z0-9-_.]*$/,
                                                            message: 'Do not include @ or special characters',
                                                        },
                                                    })}
                                                />

                                                <FormInput
                                                    label="YouTube Subscribers"
                                                    type="number"
                                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                                    error={
                                                        (
                                                            errors as {
                                                                platformData?: {
                                                                    YouTube?: { followers?: { message?: string } };
                                                                };
                                                            }
                                                        ).platformData?.YouTube?.followers?.message
                                                    }
                                                    {...register('platformData.YouTube.followers', {
                                                        value: talent?.platformData?.YouTube?.followers,
                                                        pattern: {
                                                            value: /^[0-9]*$/,
                                                            message: 'Enter a valid integer without special characters',
                                                        },
                                                    })}
                                                />

                                                <FieldSeparator />

                                                <h4 className="my-3 font-semibold">Brand Prices</h4>

                                                <FormInput
                                                    label="60-90s Integration"
                                                    description="Default Brand Price"
                                                    type="number"
                                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                                    {...register('prices.brandPriceYouTubeIntegration', {
                                                        value: talent?.prices?.brandPriceYouTubeIntegration,
                                                    })}
                                                />

                                                <FormInput
                                                    label="Pre-Roll"
                                                    type="number"
                                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                                    {...register('prices.brandPriceYouTubePreroll', {
                                                        value: talent?.prices?.brandPriceYouTubePreroll,
                                                    })}
                                                />

                                                <FormInput
                                                    label="YouTube Short"
                                                    type="number"
                                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                                    {...register('prices.brandPriceYouTubeShort', {
                                                        value: talent?.prices?.brandPriceYouTubeShort,
                                                    })}
                                                />

                                                <h4 className="mt-6 mb-3 font-semibold">Song Price</h4>
                                                <FormInput
                                                    label="YT Song Price"
                                                    type="number"
                                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                                    {...register('prices.songPriceYouTube', {
                                                        value: talent?.prices?.songPriceYouTube,
                                                    })}
                                                />
                                            </>
                                        </FieldGroup>
                                    </Accordion>
                                )}

                                {isGrailTeam && (
                                    <Accordion title="Hide/Delete">
                                        <FieldGroup>
                                            <Controller
                                                control={control}
                                                name="hideUser"
                                                defaultValue={Boolean(talent.hideUser)}
                                                render={({ field }) => (
                                                    <FormCheckbox
                                                        label="Hide on Booking Platform"
                                                        description={
                                                            <>
                                                                If checked, creator will be hidden from <em>clients</em> but not from managers.
                                                            </>
                                                        }
                                                        checked={Boolean(field.value)}
                                                        onChange={field.onChange}
                                                    />
                                                )}
                                            />

                                            <Controller
                                                control={control}
                                                name="deleted"
                                                defaultValue={Boolean(talent.deleted)}
                                                render={({ field }) => (
                                                    <FormCheckbox
                                                        label="Delete from Booking Platform"
                                                        description={
                                                            <>
                                                                If checked, creator will be hidden from both clients <em>and managers</em>. USE CAREFULLY.
                                                            </>
                                                        }
                                                        checked={Boolean(field.value)}
                                                        onChange={field.onChange}
                                                    />
                                                )}
                                            />

                                            {/* <Field>
                                                                        <FieldLabel>Show on Marketing Site?</FieldLabel>
                                                                        <div className="flex">
                                                                            <input type="checkbox" {...register("showOnWebsite", { value: talent?.showOnWebsite })} />
                                                                            <span className="ml-2 text-left text-sm">Show on <a href="https://grail-talent.com/talent" target="_blank" rel="noreferrer">https://grail-talent.com/talent</a></span>
                                                                        </div>
                                                                    </div> */}
                                        </FieldGroup>
                                    </Accordion>
                                )}
                            </AccordionGroup>

                            <Button type="submit" className="mt-6" onClick={checkFormValidity}>
                                Save Updates
                            </Button>
                        </form>
                    </>
                ) : null}
            </div>
        </LayoutOnboarding>
    );
}
// function preventDecimalInput(e: React.KeyboardEvent<HTMLInputElement>) {
//     if (e.key === '.') {
//         e.preventDefault();
//     }
// }

function percentageInputProps({ field, value, register }: { field: string; value?: number; register: UseFormRegister<any> }) {
    return {
        type: 'number',
        min: '0',
        max: '100',
        step: '0.01',
        // step: "1",
        onWheel: (e: React.WheelEvent<HTMLInputElement>) => (e.target as HTMLElement).blur(),
        // onKeyDown: preventDecimalInput,
        ...register(field, {
            value: value,
            setValueAs: (value: unknown) => {
                // console.log(`${field} setValueAs called with:`, value, typeof value);
                // Convert to number, but if it's an empty string or results in NaN, return null
                if (value === '' || value === null || value === undefined) {
                    return null;
                }
                const num = Number(value);
                return isNaN(num) ? null : num;
            },
        }),
    };
}
